import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { BarcodeFormat as ZxingBarcodeFormat } from "@zxing/library";
import { BarcodeFormat } from "barcode-detector";
import { useContext, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import Text from "~/src/components/Text/Text";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import BarcodeReader, { getBarcodeReader } from "~/src/helpers/BarcodeReader";
import { sentryLogError } from "~/src/helpers/sentry";
import { useLocalStateful } from "~/src/hooks/useLocalState";
import { useReload } from "~/src/hooks/useReload";
import { Stateful, useStateful } from "~/src/hooks/useStateful";

const MAX_HANDLE_ERROR_RECURSIONS = 5;
const DEFAULT_MEDIA_TRACK_CONSTRAINTS = {
  aspectRatio: { ideal: 0.9 },
  advanced: [
    { facingMode: { exact: "environment" } },
    { zoom: { ideal: 1 } },
    { focusMode: { ideal: "continuous" } },
  ],
};

type BarcodeScannerProps = {
  onResult?: (result: string) => void;
  enabled: Stateful<boolean>;
  scanIntervalMilliseconds?: number;
  barcodeFormats?: BarcodeFormat[];
  requestedCameraCapabilities?: MediaTrackConstraints;
};

type CameraInfo = Omit<MediaDeviceInfo, "toJSON" | "kind">;

const cameraInfoToString = (camera: CameraInfo) =>
  JSON.stringify([camera.label, camera.deviceId, camera.groupId]);
const cameraInfoFromString = (camera: string) => {
  const parsed = JSON.parse(camera);
  return {
    label: parsed[0],
    deviceId: parsed[1],
    groupId: parsed[2],
  };
};

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onResult,
  enabled,
  scanIntervalMilliseconds = 25,
  barcodeFormats = undefined,
  requestedCameraCapabilities = DEFAULT_MEDIA_TRACK_CONSTRAINTS,
}: BarcodeScannerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoStream = useRef<MediaStream | null>(null);
  const barcodeReader = useStateful<BarcodeReader | undefined>(undefined);
  const cameraAccessDenied = useStateful(false);
  const availableCameras = useStateful<CameraInfo[]>([]);
  const selectedCamera = useStateful<CameraInfo | undefined>(undefined);

  const preferredCameraLabel = useLocalStateful<string | undefined>(
    { id: "preferredCamera" },
    undefined,
  );

  // gum er 'get user media', som henter ut brukerens enheter.
  // Om flere instanser av denne kjøres samtidig kan det føre til at kameraet vises som å være
  // 'i bruk' selv når appen ikke bruker det
  const gumInProgress = useRef(false);

  // labels som skal settes som default hvis de finnes, og hvis bruker ikke har valgt kamera selv
  const defaultPreferredLabels = ["camera2 0, facing back"];

  const handleErrorRecursions = useStateful(0);
  const ui = useContext(UIContext);
  const lastError = useStateful<Error | undefined>(undefined);
  const reload = useReload("BarcodeScanner");
  const { t } = useTranslation();

  const videoFilter = (device: MediaDeviceInfo) => {
    return device.kind === "videoinput" && device.label !== ""; // På iOS returneres et tomt objekt hvis kamera ikke er tilgjengelig
  };

  const [streamRev, setStreamRev] = useState(0);

  const handleError = (error: Error) => {
    lastError.set(error);
    resetAll();
    handleErrorRecursions.set(handleErrorRecursions.value + 1);
    if (["NotAllowedError", "PermissionDeniedError"].includes(error.name)) {
      cameraAccessDenied.set(true);
      ui.showSnackbar({
        message: t("barcodeScanner.canNotActivate"),
        title: error.name,
        severity: AlertVariant.Error,
      });
    } else if (
      ["NotReadableError"].includes(error.name) ||
      error.message == "Could not start video source"
    ) {
      ui.showSnackbar({
        message: t("barcodeScanner.cameraInUse"),
        title: error.name,
        severity: AlertVariant.Warning,
      });
    } else if (["WaitingForPermissions"].includes(error.message)) {
      // Be om tilgang til kamera
      if (gumInProgress.current) {
        return;
      }
      gumInProgress.current = true;

      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          stopStream(stream);
          return getAvailableCameras();
        })
        .catch((error) => {
          handleError(error);
        })
        .finally(() => {
          gumInProgress.current = false;
        });
    } else {
      sentryLogError(error);
      ui.showSnackbar({
        message: error.message,
        title: error.name,
        severity: AlertVariant.Warning,
      });
    }
  };

  useEffect(() => {
    // reload etter bruk av navigeringsknappene i nettleser
    window.history.pushState(null, "", window.location.pathname);

    window.onbeforeunload = reload.reload;

    window.addEventListener("popstate", () => reload.reload);
    return () => {
      window.removeEventListener("popstate", () => reload.reload);
    };
  }, [reload]);

  useEffect(() => {
    if (handleErrorRecursions.value >= MAX_HANDLE_ERROR_RECURSIONS) {
      ui.showSnackbar({ message: lastError.value?.message || "", severity: AlertVariant.Warning });
    }
  }, [handleErrorRecursions.value]);

  // Fetch available cameras
  const getAvailableCameras = async () => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const allCameras = devices
          .filter((device) => videoFilter(device))
          .map((device) => {
            if (!preferredCameraLabel.value) {
              // sett preferredCameraLabel her under utlisting, hvis den ikke er satt av bruker
              if (defaultPreferredLabels.includes(device.label)) {
                preferredCameraLabel.set(device.label);
              }
            }
            return {
              label: device.label,
              deviceId: device.deviceId,
              groupId: device.groupId,
            };
          });
        if (allCameras.length === 0) {
          throw new Error("WaitingForPermissions");
        }
        availableCameras.set(allCameras);
      })
      .catch((err) => {
        handleError(err);
      });
  };

  useEffect(() => {
    if (enabled.value && !cameraAccessDenied.value) {
      getAvailableCameras();
    }
  }, [cameraAccessDenied.value, enabled.value]);

  useEffect(() => {
    // Cleanup to stop active camera on page exit
    return () => {
      resetAll();
    };
  }, []);

  useEffect(() => {
    const findBestCameraMatch = () => {
      getInitialCamera().then((stream) => {
        const track = stream?.getVideoTracks().at(0)?.getCapabilities();
        const label = availableCameras.value.find(
          (device: CameraInfo) => device.deviceId === track?.deviceId,
        )?.label;
        const deviceInfo = {
          deviceId: track?.deviceId || "",
          groupId: track?.groupId || "",
          label: label || "",
        };
        selectedCamera.set(deviceInfo);
        if (stream) {
          stopStream(stream);
        }
      });
    };

    if (availableCameras.value.length === 1) {
      // hvis det finnes bare ett kamera så velger vi det
      selectedCamera.set(availableCameras.value[0]);
    } else if (availableCameras.value.length > 1) {
      // hvis det finnes flere kamera
      const preferredCamera: CameraInfo | undefined =
        preferredCameraLabel.value !== undefined
          ? availableCameras.value.find(
              (device: CameraInfo) => device.label === preferredCameraLabel.value,
            )
          : undefined;
      if (preferredCamera) {
        // velg det kameraet som er valgt av bruker (eller defaultPreferredLabels)
        selectedCamera.set(preferredCamera);
      } else {
        // eller det kameraet som passer best
        findBestCameraMatch();
      }
    }
  }, [availableCameras.value]);

  const getInitialCamera = async (): Promise<MediaStream | void> => {
    if (gumInProgress.current) {
      return;
    }
    // finner kamera som passer best med requestedCameraCapabilities / constraints
    gumInProgress.current = true;

    return navigator.mediaDevices
      .getUserMedia({
        video: requestedCameraCapabilities,
        audio: false,
      })
      .then((stream) => {
        if (videoStream.current) {
          stopStream(videoStream.current);
        }
        return stream;
      })
      .catch((error) => handleError(error))
      .finally(() => {
        gumInProgress.current = false;
      });
  };

  const applyConstraints = async () => {
    // forsøk å sett zoom og auto-focus på valgt kamera
    if (!videoStream.current) return;

    const videoTrack = videoStream.current.getVideoTracks().at(0);

    if (!videoTrack || videoTrack.readyState !== "live" || !videoTrack.enabled) {
      return;
    }

    const zoomOne = {
      advanced: [{ zoom: 1 }],
    };
    const autoFocus = {
      advanced: [{ focusMode: "continuous" }],
    };
    const torchOn = {
      advanced: [{ torch: true }],
    };

    if (videoTrack && "getCapabilities" in videoTrack) {
      const capabilities = videoTrack.getCapabilities();
      if (capabilities && "zoom" in capabilities) {
        // @ts-ignore zoom is a possible capability
        await videoTrack.applyConstraints(zoomOne).catch((e) => console.error(e));
      }
      const focusMode =
        "focusMode" in capabilities ? (capabilities.focusMode as string[]) : undefined;

      if (focusMode && focusMode.includes("continuous")) {
        // @ts-ignore focusMode is a possible capability
        await videoTrack.applyConstraints(autoFocus).catch((e) => console.error(e));
      }

      if (torchOn && "torch" in capabilities) {
        // @ts-ignore torch is a possible capability
        await videoTrack.applyConstraints(torchOn).catch((e) => console.error(e));
      }
    }
  };

  const stopStream = (stream: MediaStream | null) => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        // @ts-ignore
        track.applyConstraints({ advanced: [{ torch: false }] }).catch(() => {
          // do nothing
        });
        track.stop();
      });
    }
  };

  const resetAll = () => {
    stopStream(videoStream.current);
    if (videoRef.current?.srcObject && "getTracks" in videoRef.current.srcObject) {
      stopStream(videoRef.current.srcObject as MediaStream);
    }
    videoStream.current = null;
    barcodeReader.set(undefined);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    // hent videostrøm fra valgt kamera
    if (
      enabled.value &&
      videoRef.current !== null &&
      selectedCamera.value !== undefined &&
      availableCameras.value.length > 0
    ) {
      resetAll();
      if (gumInProgress.current) {
        return;
      }
      gumInProgress.current = true;

      navigator.mediaDevices
        .getUserMedia({
          video: {
            deviceId: { exact: selectedCamera.value.deviceId },
            groupId: { ideal: selectedCamera.value.groupId },
            aspectRatio: { ideal: 0.9 },
          },
        })
        .then((stream) => {
          setTimeout(() => {
            if (videoStream.current) {
              stopStream(videoStream.current);
            }
            videoStream.current = stream;

            setStreamRev((v) => v + 1);
          }, 500);
        })
        .catch((error) => {
          handleError(error);
        })
        .finally(() => {
          gumInProgress.current = false;
        });
    }
  }, [enabled.value, selectedCamera.value, videoRef.current, availableCameras.value]);

  useEffect(() => {
    // aktiver barcode reader for aktiv videostrøm
    if (enabled.value && videoRef.current !== null && videoStream.current !== null) {
      videoRef.current.srcObject = videoStream.current;
      getBarcodeReader(barcodeFormats)
        .then((reader: BarcodeReader) => {
          barcodeReader.set(reader);
        })
        .catch((e: Error) => handleError(e));
    }
  }, [enabled.value, streamRev, barcodeFormats]);

  useEffect(() => {
    if (!enabled.value) {
      resetAll();
    }
  }, [enabled.value]);

  useEffect(() => {
    if (!enabled.value || !barcodeReader.value || !videoRef.current) return;

    const handleResult = (result: string) => {
      onResult?.(result);
      enabled.set(false);
    };

    // skanneloop
    const scan = () => {
      const reader = barcodeReader.value;
      const video = videoRef.current;

      if (reader !== undefined && video) {
        reader
          .detect(video)
          .then((res) => handleResult(res))
          .catch(() => {
            let fallback = undefined;
            try {
              fallback = reader.zxingDetect(video, [ZxingBarcodeFormat.CODE_128]);
            } catch (_e) {
              // do nothing - scan again
            }
            if (fallback) {
              handleResult(fallback);
            } else {
              // do nothing - scan again
            }
          });
      }
    };

    const id = setInterval(scan, scanIntervalMilliseconds);

    return () => {
      clearInterval(id);
    };
  }, [enabled.value, barcodeReader.value]);

  useEffect(() => {
    if (videoStream.current) {
      applyConstraints();
    }
  }, [videoStream.current]);

  return (
    enabled.value && (
      <div className="w-max-screen-tablet flex flex-col h-full overflow-scroll hide-scrollbars border-2">
        <div className="flex flex-col h-full w-full justify-start p-2 tablet:p-4">
          {cameraAccessDenied.value ? (
            <Typography className="p-10 tablet:p-20 text-xl tablet:text-3xl">
              <Trans>{t("barcodeScanner.noCameraAccess", { retry: t("actions.retry") })}</Trans>
            </Typography>
          ) : (
            <div className="w-full h-full flex flex-col justify-start items-center pt-6">
              <video
                autoPlay={true}
                ref={videoRef}
                playsInline={true}
                muted={true}
                className="w-11/12"
              />
              <div className="p-4 pt-6 mb-2 tablet:mb-8 w-full border-0">
                <Accordion className="text-ldir-brown-900! shadow-none">
                  <AccordionSummary
                    className="bg-ldir-blue-100! w-full"
                    expandIcon={<ExpandMore className="text-ldir-brown-900" />}
                    aria-controls="velg-kamera-content"
                    id="velg-kamera-header"
                  >
                    <Text>{t("barcodeScanner.problemQuestion")}</Text>
                  </AccordionSummary>
                  <AccordionDetails className="bg-ldir-blue-100 p-4 flex flex-col gap-4">
                    {availableCameras.value.length > 0 && (
                      <>
                        <Text>{t("barcodeScanner.velgKamera")}</Text>
                        <Select
                          value={
                            (selectedCamera.value && cameraInfoToString(selectedCamera.value)) || ""
                          }
                          onChange={(e) => {
                            if (e.target.value) {
                              const cameraInfo = cameraInfoFromString(e.target.value);
                              selectedCamera.set(cameraInfo);
                              preferredCameraLabel.set(cameraInfo.label);
                            }
                          }}
                          fullWidth
                          className="bg-white"
                          size="small"
                        >
                          {availableCameras.value.map((camera: CameraInfo) => {
                            const cameraJSON = cameraInfoToString(camera);
                            return (
                              <MenuItem key={cameraJSON} value={cameraJSON}>
                                {camera.label || t("barcodeScanner.ukjentKamera")}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  );
};
export default BarcodeScanner;
